import type { ReactNode } from 'react';
import { Check, ChevronRight, Clock, X } from '../../ui/icons';
import { parseRiderName } from '../../../lib/riderDisplay';
import { shortRider } from '../../../lib/cyclingRound';
import { signedPoints } from '../../../lib/format';
import { cn } from '../../../lib/cn';
import type { LadderRungGrade } from './ladderTypes';
import { RiderAvatar } from './RiderAvatar';
import type { LadderRungData } from './StakeLadderRung';

function kickoffLabel(ms: number): string {
  const d = new Date(ms);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} ${time}`;
}

function riderLine(name: string, team?: string | null) {
  const { surname, firstName } = parseRiderName(name);
  return (
    <div className="truncate text-[13.5px] tracking-[-0.01em] text-ink">
      <span className="font-extrabold">{surname.toUpperCase()}</span>
      {firstName ? <span className="font-medium text-[#6E6E73]"> {firstName}</span> : null}
      {team ? <span className="text-[11.5px] font-semibold text-ink3"> · {team}</span> : null}
    </div>
  );
}

function opponentLine(
  name: string,
  nationality: string | null | undefined,
  team?: string | null
) {
  const { surname, firstName } = parseRiderName(name);
  return (
    <div className="mt-[3px] flex min-w-0 items-center gap-1.5">
      <RiderAvatar name={name} nationality={nationality} size="xs" />
      <span className="truncate text-[11.5px] tracking-[-0.01em] text-ink3">
        <span className="font-bold">{surname.toUpperCase()}</span>
        {firstName ? <span className="font-medium"> {firstName}</span> : null}
        {team ? <span className="font-medium"> · {team}</span> : null}
      </span>
    </div>
  );
}

function rowTone(grade: LadderRungGrade | undefined, stageLive: boolean): string {
  if (!grade) return '';
  if (grade.status === 'void') return 'bg-[#F4F4F7] ring-1 ring-inset ring-black/[0.05]';
  if (grade.status === 'settled' && grade.correct) return 'bg-accent-wash/55 ring-1 ring-inset ring-accent/12';
  if (grade.status === 'settled' && grade.correct === false) return 'bg-[rgba(210,84,79,0.07)] ring-1 ring-inset ring-miss/10';
  if (stageLive && grade.status === 'upcoming') return 'bg-[rgba(227,168,43,0.08)] ring-1 ring-inset ring-[#E3A82B]/12';
  return '';
}

/**
 * Read-only ladder rung — 64px row height aligned with the weight pill column.
 * Won/lost/live states use inset rounded tiles, not full-width square blocks.
 */
export function StakeLadderRungReadOnly({
  data,
  grade,
  stageLive = false,
  highlighted = false,
  onPress
}: {
  data: LadderRungData;
  grade?: LadderRungGrade;
  stageLive?: boolean;
  highlighted?: boolean;
  onPress?: () => void;
}) {
  const winnerSide = data.pick;
  const winner = winnerSide === 'B' ? data.fixture.riderB : data.fixture.riderA;
  const loser = winnerSide === 'B' ? data.fixture.riderA : data.fixture.riderB;
  const winnerName = winner?.riderName ?? (winnerSide === 'B' ? data.fixture.awayTeam : data.fixture.homeTeam);
  const loserName = loser?.riderName ?? (winnerSide === 'B' ? data.fixture.homeTeam : data.fixture.awayTeam);

  const won = grade?.status === 'settled' && !!grade.correct;
  const lost = grade?.status === 'settled' && grade.correct === false;
  const decided = grade?.status === 'settled' || grade?.status === 'void';
  const tone = rowTone(grade, stageLive);

  const inner = (
    <>
      <RiderAvatar name={winnerName} nationality={winner?.nationality} size="lg" />

      <div className="min-w-0 flex-1">
        {riderLine(winnerName, winner?.teamName)}
        {opponentLine(loserName, loser?.nationality, loser?.teamName)}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {decided && grade?.points != null ? (
          <span className={cn('num text-[16px] font-extrabold leading-none', won ? 'text-accent-ink' : lost ? 'text-miss' : 'text-ink')}>
            {signedPoints(grade.points)}
          </span>
        ) : grade && !decided && stageLive ? (
          <Clock width={14} height={14} strokeWidth={2.2} className="text-[#B5740A]" />
        ) : null}
        {onPress ? (
          <ChevronRight
            width={15}
            height={15}
            strokeWidth={2.4}
            className={cn(highlighted ? 'text-accent-ink' : 'text-ink3')}
          />
        ) : null}
      </div>
    </>
  );

  const row = cn(
    'relative flex min-h-[64px] w-full items-center gap-[11px] rounded-[14px] px-1.5 py-2 pr-2.5',
    tone
  );

  const wrap = cn('relative w-full py-[2px] text-left first:pt-0 last:pb-0');

  if (onPress) {
    return (
      <div className={wrap} data-highlighted={highlighted || undefined}>
        <button
          type="button"
          onClick={onPress}
          className={cn(row, 'tap text-left active:scale-[0.995] active:opacity-95')}
        >
          {inner}
        </button>
      </div>
    );
  }

  return (
    <div className={wrap} data-highlighted={highlighted || undefined}>
      <div className={row}>{inner}</div>
    </div>
  );
}

/** Duel detail card — rendered below the ladder so rows stay one rank tall. */
export function LadderDuelDetailPanel({
  data,
  grade,
  stageLive = false
}: {
  data: LadderRungData;
  grade: LadderRungGrade;
  stageLive?: boolean;
}) {
  const won = grade.status === 'settled' && !!grade.correct;
  const lost = grade.status === 'settled' && grade.correct === false;
  const voided = grade.status === 'void';
  const live = data.fixture.status === 'LIVE';
  const settled = data.fixture.status === 'SETTLED' || data.fixture.status === 'VOID';

  return (
    <div className="border-t border-black/[0.06] bg-[#FAFAFB] px-4 py-3">
      <div className="flex items-center gap-1.5 text-[9px] font-extrabold uppercase tracking-[0.1em] text-accent-ink">
        <span className="h-1.5 w-1.5 rounded-full bg-accent" />
        Selected duel
      </div>

      <div className="mt-2 overflow-hidden rounded-[14px] border border-black/[0.08] bg-white px-3 py-3 shadow-tile">
        {grade.pickedName && grade.otherName && (
          <p className="text-[14px] font-extrabold leading-snug text-ink">
            You backed {shortRider(grade.pickedName)} over {shortRider(grade.otherName)}
          </p>
        )}

        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {voided ? (
            <GradeChip tone="muted">Void</GradeChip>
          ) : won ? (
            <GradeChip tone="win">
              <Check width={10} height={10} strokeWidth={3.2} /> Won
            </GradeChip>
          ) : lost ? (
            <GradeChip tone="loss">
              <X width={10} height={10} strokeWidth={3.2} /> Lost
            </GradeChip>
          ) : live || stageLive ? (
            <GradeChip tone="live">
              <Clock width={10} height={10} strokeWidth={2.2} /> Live
            </GradeChip>
          ) : (
            <GradeChip tone="muted">{kickoffLabel(data.fixture.kickoffAt)}</GradeChip>
          )}
          {grade.confidence != null && (
            <span className="num text-[12px] font-extrabold text-ink2">×{grade.confidence} stake</span>
          )}
          {grade.points != null && !voided && (
            <span className={cn('num ml-auto text-[15px] font-extrabold', won ? 'text-accent-ink' : lost ? 'text-miss' : 'text-ink')}>
              {signedPoints(grade.points)} pts
            </span>
          )}
        </div>

        {!settled && !live && !stageLive && (
          <p className="mt-2.5 text-[11px] font-medium text-ink3">Waiting for the stage to roll out.</p>
        )}
      </div>
    </div>
  );
}

function GradeChip({ tone, children }: { tone: 'win' | 'loss' | 'live' | 'muted'; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-[6px] px-2 py-[3px] text-[10px] font-extrabold',
        tone === 'win' && 'bg-accent-wash text-accent-ink',
        tone === 'loss' && 'bg-[rgba(210,84,79,0.12)] text-miss',
        tone === 'live' && 'bg-[rgba(227,168,43,0.15)] text-[#B5740A]',
        tone === 'muted' && 'bg-[#EFEFF3] text-ink3'
      )}
    >
      {children}
    </span>
  );
}
